import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";
import { writingErrorsData } from "@/data/mockData";
import { streakService } from "./streakService";
import { buildErrorDuplicateKey, getValidUuidOrNull, isUniqueConstraintError, toNullableUuid, handleSupabaseError } from "@/utils/duplicateUtils";
import { flashcardService } from "./flashcardService";
import { getCurrentUserIdOrThrow } from "@/services/authService";
import { profileService } from "./profileService";
import { activityService } from "./activityService";


export interface WritingErrorRecord {
  id: string;
  source?: string;
  sourceCorrectionId?: string; // maps to source_test_id
  sourceErrorId?: string;      // maps to source_question_id
  category: string;
  original: string;            // maps to original_sentence
  correction: string;          // maps to corrected_sentence
  corrected?: string;          // maps to corrected_sentence
  explanation: string;
  severity?: "low" | "medium" | "high" | string;
  context?: string;            // maps to literary_context
  competency?: "writing" | string; // maps to competence
  section?: string;
  createdAt?: string;
  tags?: any;
  metadata?: any;
}

const STORAGE_KEY = "tef_writing_errors";

export function mapSupabaseToError(row: any): WritingErrorRecord {
  return {
    id: row.id,
    category: row.category || "Concordância",
    competency: row.competence || "writing",
    section: row.section || "",
    original: row.original_sentence || "",
    correction: row.corrected_sentence || "",
    corrected: row.corrected_sentence || "",
    explanation: row.explanation || "",
    context: row.literary_context || "",
    sourceCorrectionId: row.source_test_id || undefined,
    sourceErrorId: row.source_question_id || undefined,
    severity: row.metadata?.severity || "medium",
    createdAt: row.created_at
  };
}

export function mapErrorToSupabase(err: any): any {
  const dupKey = buildErrorDuplicateKey(err);

  let sanitizedTags: string[] = [];
  if (typeof err.tags === "string") {
    sanitizedTags = (err.tags as string).split(",").map(t => t.trim()).filter(Boolean);
  } else if (Array.isArray(err.tags)) {
    sanitizedTags = err.tags.map((t: any) => String(t).trim()).filter(Boolean);
  }

  let sanitizedMetadata: any = {};
  if (typeof err.metadata === "string") {
    try {
      sanitizedMetadata = JSON.parse(err.metadata);
    } catch {
      sanitizedMetadata = { note: err.metadata };
    }
  } else if (err.metadata && typeof err.metadata === "object") {
    sanitizedMetadata = { ...err.metadata };
  }
  sanitizedMetadata.severity = err.severity || sanitizedMetadata.severity || "medium";

  return {
    category: err.category || "Concordância",
    competence: err.competency || err.competence || "writing",
    section: err.section || "",
    original_sentence: (err.original || "").trim(),
    corrected_sentence: (err.corrected || err.correction || "").trim(),
    explanation: err.explanation || "",
    literary_context: err.context || err.original || "",
    source_question_id: toNullableUuid(err.sourceErrorId),
    source_test_id: toNullableUuid(err.sourceCorrectionId),
    tags: sanitizedTags,
    metadata: sanitizedMetadata,
    duplicate_key: dupKey
  };
}

export function isDuplicateErrorBankError(error: any): boolean {
  return isUniqueConstraintError(error);
}

export const errorBankService = {
  // Get all registered errors
  async getErrors(): Promise<WritingErrorRecord[]> {
    if (!isSupabaseConfigured || !supabase) {
      return this.getLocalErrors();
    }
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        return [];
      }
      const { data, error } = await supabase
        .from("error_bank")
        .select("*")
        .eq("user_id", userData.user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []).map(mapSupabaseToError);
    } catch (err: any) {
      throw handleSupabaseError(err, "error");
    }
  },

  // Get single error details
  async getErrorById(id: string): Promise<WritingErrorRecord | null> {
    if (!isSupabaseConfigured || !supabase) {
      const local = this.getLocalErrors();
      return local.find(e => e.id === id) || null;
    }
    try {
      if (id.startsWith("err_") || !getValidUuidOrNull(id)) {
        const local = this.getLocalErrors();
        return local.find(e => e.id === id) || null;
      }
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        return null;
      }
      const { data, error } = await supabase
        .from("error_bank")
        .select("*")
        .eq("id", id)
        .eq("user_id", userData.user.id)
        .single();

      if (error) throw error;
      return data ? mapSupabaseToError(data) : null;
    } catch (err: any) {
      throw handleSupabaseError(err, "error");
    }
  },

  // Create error record
  async createError(errorRecord: Omit<WritingErrorRecord, "id"> & { id?: string }): Promise<WritingErrorRecord> {
    streakService.registerActivity();
    const dupKey = buildErrorDuplicateKey(errorRecord);

    if (!isSupabaseConfigured || !supabase) {
      const local = this.getLocalErrors();
      const isDup = local.some(e => buildErrorDuplicateKey(e) === dupKey);
      if (isDup) {
        throw new Error("Este erro já está cadastrado no Banco de Erros.");
      }
      if (local.length >= 50) {
        throw new Error("TRIAL_LIMIT_EXCEEDED: error_bank");
      }
      return this.createLocalError(errorRecord);
    }

    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("Usuário não autenticado. Não é possível salvar erro.");
      }

      console.log("[ErrorBankService] creating error", errorRecord);
      
      if (!errorRecord.original || !errorRecord.original.trim()) {
        throw new Error("A frase original não pode estar vazia.");
      }
      if (!(errorRecord.corrected || errorRecord.correction || "").trim()) {
        throw new Error("A frase corrigida não pode estar vazia.");
      }

      // Direct lookup check
      const { data: existing, error: findError } = await supabase
        .from("error_bank")
        .select("id")
        .eq("duplicate_key", dupKey)
        .eq("user_id", userData.user.id)
        .maybeSingle();

      if (existing) {
        throw new Error("Este erro já está cadastrado no Banco de Erros.");
      }

      // Check limits
      const profile = await profileService.fetchOrCreateProfile(userData.user.id, userData.user.email || "");
      if (profile && (profile.plan === "free" || profile.plan === "trial" || profile.plan === "expired")) {
        const { count, error: countError } = await supabase
          .from("error_bank")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userData.user.id);
        
        if (countError) throw countError;
        
        if ((count || 0) >= 50) {
          throw new Error("TRIAL_LIMIT_EXCEEDED: error_bank");
        }
      }

      const row = {
        ...mapErrorToSupabase(errorRecord),
        user_id: userData.user.id
      };
      const { data, error } = await supabase
        .from("error_bank")
        .insert([row])
        .select()
        .single();

      if (error) throw error;
      console.log("[ErrorBankService] created error", data);

      // Track activity log
      try {
        await activityService.trackUserActivity(
          "error_saved",
          "error",
          data?.id || "",
          { category: errorRecord.category, severity: errorRecord.severity }
        );
      } catch (err) {
        console.warn("Failed to track activity:", err);
      }

      this.emitUpdates();
      return mapSupabaseToError(data);
    } catch (err: any) {
      if (err.message === "Este erro já está cadastrado no Banco de Erros." || err.message === "A frase original não pode estar vazia." || err.message === "A frase corrigida não pode estar vazia." || err.message?.includes("Usuário não autenticado") || err.message === "TRIAL_LIMIT_EXCEEDED: error_bank") {
        throw err;
      }
      throw handleSupabaseError(err, "error");
    }
  },

  // Update error record
  async updateError(id: string, errorRecord: Partial<WritingErrorRecord>): Promise<WritingErrorRecord> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      throw new Error("Usuário não autenticado. Não é possível salvar erro.");
    }

    const current = await this.getErrorById(id);
    if (!current) throw new Error("Error record not found");

    const merged = { ...current, ...errorRecord };
    const dupKey = buildErrorDuplicateKey(merged);

    if (!isSupabaseConfigured || !supabase || id.startsWith("err_") || !getValidUuidOrNull(id)) {
      const local = this.getLocalErrors();
      const isDup = local.some(e => e.id !== id && buildErrorDuplicateKey(e) === dupKey);
      if (isDup) {
        throw new Error("Este erro já está cadastrado no Banco de Erros.");
      }
      return this.updateLocalError(id, errorRecord);
    }

    try {
      if (!merged.original || !merged.original.trim()) {
        throw new Error("A frase original não pode estar vazia.");
      }
      if (!(merged.corrected || merged.correction || "").trim()) {
        throw new Error("A frase corrigida não pode estar vazia.");
      }

      // Unique check
      const { data: existing, error: findError } = await supabase
        .from("error_bank")
        .select("id")
        .eq("duplicate_key", dupKey)
        .eq("user_id", userData.user.id)
        .neq("id", id)
        .maybeSingle();

      if (existing) {
        throw new Error("Este erro já está cadastrado no Banco de Erros.");
      }

      const row = {
        ...mapErrorToSupabase(merged),
        user_id: userData.user.id
      };
      const { data, error } = await supabase
        .from("error_bank")
        .update(row)
        .eq("id", id)
        .eq("user_id", userData.user.id)
        .select()
        .single();

      if (error) throw error;
      this.emitUpdates();
      return mapSupabaseToError(data);
    } catch (err: any) {
      if (err.message === "Este erro já está cadastrado no Banco de Erros." || err.message === "A frase original não pode estar vazia." || err.message === "A frase corrigida não pode estar vazia.") {
        throw err;
      }
      throw handleSupabaseError(err, "error");
    }
  },

  // Delete error record
  async deleteError(id: string): Promise<void> {
    if (!isSupabaseConfigured || !supabase || id.startsWith("err_") || !getValidUuidOrNull(id)) {
      return this.deleteLocalError(id);
    }
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("Usuário não autenticado. Não é possível salvar erro.");
      }

      const { error } = await supabase
        .from("error_bank")
        .delete()
        .eq("id", id)
        .eq("user_id", userData.user.id);

      if (error) throw error;
      this.emitUpdates();
    } catch (err: any) {
      throw handleSupabaseError(err, "error");
    }
  },

  // Delete multiple error records
  async deleteMultipleErrors(ids: string[]): Promise<number> {
    if (!ids || ids.length === 0) return 0;

    // Split local vs database IDs
    const dbIds = ids.filter(id => !id.startsWith("err_") && getValidUuidOrNull(id));
    const localIds = ids.filter(id => id.startsWith("err_") || !getValidUuidOrNull(id));

    // Handle local deletes
    if (localIds.length > 0) {
      const list = this.getLocalErrors();
      const filtered = list.filter(e => !localIds.includes(e.id));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    }

    // Handle database deletes
    if (isSupabaseConfigured && supabase && dbIds.length > 0) {
      try {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData?.user) {
          throw new Error("Usuário não autenticado. Não é possível excluir erros.");
        }

        const { error } = await supabase
          .from("error_bank")
          .delete()
          .in("id", dbIds)
          .eq("user_id", userData.user.id);

        if (error) throw error;
      } catch (err: any) {
        throw handleSupabaseError(err, "error");
      }
    }

    this.emitUpdates();
    return ids.length;
  },

  async saveWritingV2ErrorsToErrorBank({
    task,
    correction,
    historyRecordId,
  }: {
    task: any;
    correction: any;
    historyRecordId?: string | null;
  }): Promise<{
    status: "success" | "duplicate" | "partial";
    message: string;
    insertedCount: number;
    duplicatedCount?: number;
    errors: WritingErrorRecord[];
  }> {
    console.log("[EE_V2_ERROR_BANK_SEND_START]", {
      section: task.section,
      errorCount: correction.errors?.length || 0,
    });

    if (!["Section A", "Section B"].includes(task.section)) {
      throw new Error("Seção inválida para enviar erros EE v2 ao Banco de Erros.");
    }

    if (correction.section !== task.section) {
      throw new Error("A seção da correção não corresponde à seção da tarefa.");
    }

    if (!correction.errors || !Array.isArray(correction.errors)) {
      return { status: "success", message: "Nenhum erro para salvar.", insertedCount: 0, duplicatedCount: 0, errors: [] };
    }

    if (correction.errors.length === 0) {
      return { status: "success", message: "Nenhum erro para salvar.", insertedCount: 0, duplicatedCount: 0, errors: [] };
    }

    console.log("[EE_V2_ERROR_BANK_AUTH_CHECK]", {
      section: task.section,
      errorCount: correction.errors?.length || 0,
    });

    const userId = await getCurrentUserIdOrThrow();

    console.log("[EE_V2_ERROR_BANK_USER_RESOLVED]", {
      section: task.section,
      userId,
    });

    const payloads = correction.errors.map((error: any) => {
      const context = String(error.contextSentence || error.original || "").trim();
      const sanitizedContext = context.length > 500 ? context.slice(0, 500) + "..." : context;

      if (context.length > 500) {
        console.warn("[EE_V2_ERROR_BANK_LONG_CONTEXT_TRUNCATED]", {
          originalLength: context.length,
        });
      }

      const rawPayload = {
        category: error.category || "Concordância",
        competence: "Expression Écrite (EE)",
        section: task.section || "Section A",
        original: error.original,
        corrected: error.corrected,
        explanation: error.explanation,
        context: sanitizedContext,
        severity: error.severity || "medium",
        metadata: {
          writingVersion: 2,
          correctionType: "linguistic_feedback",
          historyRecordId: historyRecordId || null,
          taskId: task.id,
          taskTitle: task.title || "",
          prompt: task.prompt || "",
          createdFrom: `writing_v2_${String(task.section || "Section A").toLowerCase().replace(" ", "_")}`,
          flashcardSuggestion: error.flashcardSuggestion || null,
          source: "ee_v2_pedagogical_correction",
        }
      };

      const mapped = mapErrorToSupabase(rawPayload);
      return {
        ...mapped,
        user_id: userId
      };
    });

    // Handle Local Fallback Mode
    if (!isSupabaseConfigured || !supabase) {
      const local = this.getLocalErrors();
      let insertedCount = 0;
      let duplicatedCount = 0;
      const insertedRecords: WritingErrorRecord[] = [];

      payloads.forEach((payload: any) => {
        const mappedErrorRecord = {
          category: payload.category,
          competency: payload.competence,
          section: payload.section,
          original: payload.original_sentence,
          corrected: payload.corrected_sentence,
          explanation: payload.explanation,
          context: payload.literary_context,
          severity: payload.metadata?.severity || "medium",
          metadata: payload.metadata,
        };

        const dupKey = buildErrorDuplicateKey(mappedErrorRecord);
        const isDup = local.some(e => buildErrorDuplicateKey(e) === dupKey);
        
        if (isDup) {
          duplicatedCount++;
          console.log("[EE_V2_ERROR_BANK_DUPLICATE_DETECTED]", {
            section: task.section,
            message: "Local storage duplicate detected",
          });
        } else {
          const created = this.createLocalError(mappedErrorRecord);
          insertedCount++;
          insertedRecords.push(created);
        }
      });

      this.emitUpdates();

      let status: "success" | "duplicate" | "partial" = "success";
      let message = "Erros enviados ao Banco de Erros.";
      if (insertedCount === 0) {
        status = "duplicate";
        message = "Os erros desta correção já estão no Banco de Erros.";
      } else if (duplicatedCount > 0) {
        status = "partial";
        message = "Alguns erros novos foram enviados. Outros já existiam no Banco de Erros.";
      }

      console.log("[EE_V2_ERROR_BANK_SEND_RESULT]", {
        status,
        insertedCount,
        duplicatedCount,
      });

      return {
        status,
        message,
        insertedCount,
        duplicatedCount,
        errors: insertedRecords,
      };
    }

    // Online Database Mode: Process individually to prevent batch insertion failure due to any duplicate constraint
    let insertedCount = 0;
    let duplicatedCount = 0;
    const insertedRecords: WritingErrorRecord[] = [];
    let lastError: any = null;

    for (const payload of payloads) {
      try {
        const { data, error } = await supabase
          .from("error_bank")
          .insert([payload])
          .select();

        if (error) {
          if (isDuplicateErrorBankError(error)) {
            console.log("[EE_V2_ERROR_BANK_DUPLICATE_DETECTED]", {
              section: task.section,
              message: error.message || error.details,
            });
            duplicatedCount++;
          } else {
            console.error("[EE_V2_ERROR_BANK_ERROR_RAW]", error);
            lastError = error;
          }
        } else {
          insertedCount++;
          if (data && data[0]) {
            insertedRecords.push(mapSupabaseToError(data[0]));
          }
        }
      } catch (err: any) {
        if (isDuplicateErrorBankError(err)) {
          console.log("[EE_V2_ERROR_BANK_DUPLICATE_DETECTED]", {
            section: task.section,
            message: err.message,
          });
          duplicatedCount++;
        } else {
          console.error("[EE_V2_ERROR_BANK_ERROR_RAW]", err);
          lastError = err;
        }
      }
    }

    if (lastError && insertedCount === 0 && duplicatedCount === 0) {
      throw lastError;
    }

    this.emitUpdates();

    let status: "success" | "duplicate" | "partial" = "success";
    let message = "Erros enviados ao Banco de Erros.";
    if (insertedCount === 0) {
      status = "duplicate";
      message = "Os erros desta correção já estão no Banco de Erros.";
    } else if (duplicatedCount > 0) {
      status = "partial";
      message = "Alguns erros novos foram enviados. Outros já existiam no Banco de Erros.";
    }

    console.log("[EE_V2_ERROR_BANK_SEND_RESULT]", {
      status,
      insertedCount,
      duplicatedCount,
    });

    return {
      status,
      message,
      insertedCount,
      duplicatedCount,
      errors: insertedRecords,
    };
  },

  // Create flashcard from error
  async createFlashcardFromError(errorId: string): Promise<any> {
    streakService.registerActivity();
    const err = await this.getErrorById(errorId);
    if (!err) throw new Error("Error record not found");
    
    const meta = err.metadata || {};
    
    // Se for um erro da EE v2, usar o método específico
    if (meta.writingVersion === 2 || err.source === "ee_v2_pedagogical_correction") {
      console.log("[errorBankService] Creating flashcard for EE v2 error");
      return flashcardService.createFlashcardFromWritingV2Error({
        error: {
          id: errorId,
          original: err.original,
          corrected: err.correction || err.corrected,
          explanation: err.explanation,
          category: err.category,
          severity: err.severity,
          contextSentence: err.context,
          flashcardSuggestion: meta.flashcardSuggestion
        },
        task: {
          id: meta.taskId,
          title: meta.taskTitle,
          prompt: meta.prompt
        },
        historyRecordId: meta.historyRecordId
      });
    }

    const front = err.original;
    const back = `${err.correction || err.corrected}\n\n${err.explanation}`;
    const category = err.category || "Grammaire";

    const cardPayload = {
      front,
      back,
      type: "error",
      language: "fr",
      category,
      source: "error_bank",
      sourceErrorId: errorId,
      metadata: {
        originalSentence: err.original,
        correctedSentence: err.correction || err.corrected,
        explanation: err.explanation,
        category: err.category,
        competence: err.competency,
        section: err.section
      }
    };

    return flashcardService.createFlashcard(cardPayload as any);
  },

  // --- LOCAL FALLBACK METHODS ---
  getLocalErrors(): WritingErrorRecord[] {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(writingErrorsData));
      return writingErrorsData as WritingErrorRecord[];
    }
    try {
      return JSON.parse(raw);
    } catch {
      return writingErrorsData as WritingErrorRecord[];
    }
  },

  createLocalError(errorRecord: Omit<WritingErrorRecord, "id"> & { id?: string }): WritingErrorRecord {
    const list = this.getLocalErrors();
    const newItem: WritingErrorRecord = {
      ...errorRecord,
      id: errorRecord.id || "err_" + Date.now() + "_" + Math.random().toString(36).slice(2, 5)
    };
    list.unshift(newItem);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    this.emitUpdates();
    return newItem;
  },

  updateLocalError(id: string, partial: Partial<WritingErrorRecord>): WritingErrorRecord {
    const list = this.getLocalErrors();
    const idx = list.findIndex(e => e.id === id);
    if (idx === -1) {
      throw new Error(`Error ID ${id} not found locally.`);
    }
    const updated = { ...list[idx], ...partial };
    list[idx] = updated;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    this.emitUpdates();
    return updated;
  },

  deleteLocalError(id: string): void {
    const list = this.getLocalErrors();
    const filtered = list.filter(e => e.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    this.emitUpdates();
  },

  emitUpdates() {
    window.dispatchEvent(new Event("errorsUpdated"));
    window.dispatchEvent(new Event("dashboardUpdated"));
  }
};
